import React from 'react';
import { View, Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from '../usePantryName';
import { pantryUnitName } from '../../../utils/pantryDisplayName';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaZones, PaprikaShadows } from '../../../styles/theme';
import { FoodTile } from '../FoodTile';
import { FreshnessPill } from '../FreshnessPill';
import { ZONE_META } from '../zoneMeta';
import { PantryIcon } from '../PantryIcon';
import { PantryItemView } from '../../../redux/pantrySelectors';
import { PantryActions } from '../usePantryActions';
import { isCookCandidate } from '../../../services/pantryFreshnessService';
import { categoryLabelKey } from '../categoryMeta';
import { PaprikaFreshness } from '../../../styles/theme';

interface Props {
  itemId: string;
  snapshot: any;
  actions: PantryActions;
  onClose: () => void;
  onCook?: () => void;
}

export function ItemPanel({ itemId, snapshot, actions, onClose, onCook }: Props) {
  const { t, i18n } = useTranslation();
  const pantryName = usePantryName();

  const view = snapshot.all.find((v: PantryItemView) => v.item.id === itemId);
  if (!view) {
    return (
      <View style={styles.container}>
        <PanelHeader title={t('pantry.item.fallbackTitle')} onClose={onClose} />
        <Text style={styles.errorText}>{t('common.itemNotFound')}</Text>
      </View>
    );
  }

  const { item, state, days } = view;
  const zone = item.location as keyof typeof ZONE_META;
  const zoneLabel = t(`pantry.zones.${zone}`);
  const zoneMeta = ZONE_META[zone];

  const handleStillGood = () => {
    actions.stillGood(item);
    onClose();
  };

  const handlePlusThree = () => {
    actions.plusThreeDays(item);
    onClose();
  };

  const handleEaten = () => {
    actions.markEaten(item);
    onClose();
  };

  const handleTossed = () => {
    actions.markTossed(item);
    onClose();
  };

  const handleOpened = () => {
    actions.setOpened(item, !item.opened);
    onClose();
  };

  return (
    <View style={styles.container}>
      <PanelHeader
        title={pantryName(item)}
        subtitle={
          item.quantity != null
            ? `${zoneLabel} · ${item.quantity}${item.unit ? ' ' + pantryUnitName(item.unit, i18n.language, item.quantity) : ''}`
            : zoneLabel
        }
        onClose={onClose}
      />

      <View style={styles.content}>
        {/* Item display */}
        <View style={styles.itemDisplay}>
          <FoodTile
            category={item.category}
            name={item.name}
            size={56}
            radius={15}
          />
          <View style={styles.itemDetails}>
            <FreshnessPill state={state} days={days} compact={false} />
            <Text style={styles.categoryLabel}>{t(categoryLabelKey(item.category))}</Text>
          </View>
        </View>

        {/* Cook action (if applicable) */}
        {isCookCandidate(item) && (
          <Pressable
            style={[styles.button, styles.cookButton]}
            onPress={() => {
              // Task 13: Open recipes panel
              onCook?.();
              onClose();
            }}
          >
            <PantryIcon icon={{ lib: 'mci', name: 'fire' }} size={16} color={Paprika.toastText} />
            <Text style={styles.cookButtonText}>{t('pantry.cook.cookThis')}</Text>
          </Pressable>
        )}

        {/* Update eyebrow */}
        <Text style={styles.updateEyebrow}>{t('pantry.item.update')}</Text>

        {/* Action grid (2x2) */}
        <View style={styles.actionGrid}>
          <Pressable
            style={[styles.gridButton, { backgroundColor: PaprikaFreshness.fresh.bg }]}
            onPress={handleStillGood}
          >
            <Feather name="check" size={17} color={PaprikaFreshness.fresh.text} />
            <Text style={[styles.gridButtonText, { color: PaprikaFreshness.fresh.text }]}>
              {t('pantry.item.stillGood')}
            </Text>
          </Pressable>

          <Pressable style={styles.gridButton} onPress={handlePlusThree}>
            <Feather name="refresh-cw" size={17} color={Paprika.textSecondary} />
            <Text style={[styles.gridButtonText, { color: Paprika.textSecondary }]}>
              {t('pantry.item.plusThreeDays')}
            </Text>
          </Pressable>

          <Pressable style={styles.gridButton} onPress={handleEaten}>
            <Feather name="check" size={17} color={Paprika.textSecondary} />
            <Text style={[styles.gridButtonText, { color: Paprika.textSecondary }]}>
              {t('pantry.item.eatenDone')}
            </Text>
          </Pressable>

          <Pressable
            style={[styles.gridButton, { backgroundColor: PaprikaFreshness.expired.bg }]}
            onPress={handleTossed}
          >
            <Feather name="trash-2" size={17} color={PaprikaFreshness.expired.text} />
            <Text style={[styles.gridButtonText, { color: PaprikaFreshness.expired.text }]}>
              {t('pantry.item.tossed')}
            </Text>
          </Pressable>
        </View>

        {/* Sealed vs opened. The single biggest lever on how long something
            keeps, and the pantry cannot see it from a photo. */}
        <Pressable
          style={styles.openedRow}
          accessibilityRole="switch"
          accessibilityState={{ checked: !!item.opened }}
          onPress={handleOpened}
        >
          <View style={styles.openedText}>
            <Text style={styles.openedLabel}>{t('pantry.item.opened')}</Text>
            <Text style={styles.openedHint}>{t('pantry.item.openedHint')}</Text>
          </View>
          <View style={[styles.openedPip, item.opened && styles.openedPipOn]}>
            {!!item.opened && <Feather name="check" size={14} color={Paprika.surfaceLowest} />}
          </View>
        </Pressable>
      </View>
    </View>
  );
}

function PanelHeader({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  return (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <Text style={styles.headerTitle}>{title}</Text>
        {!!subtitle && <Text style={styles.headerSubtitle}>{subtitle}</Text>}
      </View>
      <Pressable style={styles.closeButton} onPress={onClose}>
        <Feather name="x" size={18} color={Paprika.textSecondary} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  openedRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: 9,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceLow,
  },
  openedText: {
    flex: 1,
  },
  openedLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  openedHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  openedPip: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  openedPipOn: {
    backgroundColor: Paprika.primary,
  },
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    paddingVertical: 22,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    color: Paprika.textPrimary,
    letterSpacing: -0.015,
    lineHeight: 28,
    marginBottom: 6,
  },
  headerSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    color: Paprika.textMuted,
  },
  closeButton: {
    width: 34,
    height: 34,
    borderRadius: 999,
    backgroundColor: Paprika.surfaceHighest,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    overflow: 'scroll',
    paddingVertical: 20,
    paddingHorizontal: 24,
  },
  itemDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
  },
  itemDetails: {
    flex: 1,
  },
  categoryLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textMuted,
    marginTop: 6,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 13,
    borderRadius: 13,
    marginBottom: 16,
  },
  cookButton: {
    backgroundColor: Paprika.accent,
  },
  cookButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: Paprika.toastText,
  },
  updateEyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    fontWeight: '700',
    letterSpacing: 0.05,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    marginBottom: 10,
  },
  actionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 9,
  },
  gridButton: {
    flex: 1,
    minWidth: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 15,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceHighest,
  },
  gridButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  errorText: {
    flex: 1,
    padding: 20,
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textMuted,
  },
});
